import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import { StatusBadge, statusLabel } from '../components/StatusBadge';
import { useTableControls, SortHeader, TablePager } from '../components/TableControls';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CancelDraftButton } from '../components/CancelDraftButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { ChevronDown, ChevronUp, Eye, Warehouse, Box, MapPin, ClipboardList, Truck, Settings2, PackageSearch, Zap, Sparkles, Search, Loader2, Lock, X } from 'lucide-react';
import { platformLabel } from '../config/platforms';
import { Term, PLANNER_TERMS } from '../components/Term';
import { ConsolidationSuggestions } from '../components/ConsolidationSuggestions';
import { useCountry } from '../contexts/CountryContext';

import { useChannels } from '../hooks/useChannels';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lists are filtered/sorted/paged client-side; ask for more than the backend's default page.
const PLAN_FETCH_LIMIT = 1000;
const SHIPMENT_FETCH_LIMIT = 2000;

const PLAN_SEARCH_KEYS = ['po_number', 'po_numbers', 'fc_code', 'platform'];
const PLAN_SORT_VALUE = {
  po_number: (p) => (p.plan_type === 'FC_CONSOLIDATION' ? (p.fc_code || p.po_number) : p.po_number),
  planning_date: (p) => p.planning_date || null,
  total_suggested_units: (p) => p.total_suggested_units ?? null,
  container_fill_percentage: (p) => p.container_fill_percentage ?? null,
};
const PLAN_STATUS_FILTERS = [
  { value: 'ACTIVE', label: 'Active (hide cancelled)' },
  { value: 'ALL', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'APPROVING', label: 'Approving' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const SHIPMENT_SEARCH_KEYS = ['shipment_id', 'fc_code', 'city', 'po_number', 'po_numbers'];
const SHIPMENT_SORT_VALUE = {
  total_units: (s) => s.total_units ?? null,
  total_value: (s) => s.total_value ?? null,
};
const OPEN_SHIPMENT_STATUSES = ['PLANNED', 'DISPATCHED', 'IN_TRANSIT'];
const SHIPMENT_STATUS_FILTERS = [
  { value: 'OPEN', label: 'Open (hide delivered/cancelled)' },
  { value: 'ALL', label: 'All statuses' },
  { value: 'PLANNED', label: 'Planned' },
  { value: 'DISPATCHED', label: 'Dispatched' },
  { value: 'IN_TRANSIT', label: 'In transit' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const Loading = ({ label = 'Loading…' }) => (
  <div role="status" className="flex items-center justify-center gap-2 py-14 text-[13px] text-gray-500">
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {label}
  </div>
);

// Mirrors SHIPMENT_TRANSITIONS in backend/routes/shipment_planning.py
const NEXT_STATUSES = {
  PLANNED: ['DISPATCHED', 'CANCELLED'],
  DISPATCHED: ['IN_TRANSIT', 'DELIVERED'],
  IN_TRANSIT: ['DELIVERED'],
  DELIVERED: [],
  CANCELLED: [],
};

/** What dispatching a shipment does to stock and POs, from its items (the server re-checks everything). */
const dispatchSummary = (shipment) => {
  const items = (shipment?.items || []).filter((i) => (Number(i.quantity) || 0) > 0);
  const units = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  const skus = new Set(items.map((i) => i.sku_code).filter(Boolean)).size;
  const pos = [...new Set(items.map((i) => i.po_number || shipment?.po_number).filter(Boolean))].sort();
  const platform = String(shipment?.platform || 'AMAZON').toUpperCase();
  const target = platform.startsWith('AMAZON') ? 'Amazon Vendor' : platformLabel(platform);
  // Counts shipment units: a bundle moves its single SKU x the bundle size, and units already
  // dispatched from the Retail PO page are not moved again (the server reports what it moved).
  const text = `Moves ${units.toLocaleString('en-IN')} unit${units === 1 ? '' : 's'} of ${skus} SKU${skus === 1 ? '' : 's'} from Warehouse to ${target} stock`
    + (pos.length ? ` and marks them dispatched on PO${pos.length === 1 ? '' : 's'} ${pos.join(', ')}` : '')
    + '. Units are shipment units: a bundle moves its single SKUs, and units already dispatched from the Retail PO page are not moved again.';
  return text;
};

// Dispatch summary when the stock movement was already recorded by hand: nothing moves.
const manualDispatchSummary = (shipment) => {
  const units = (shipment?.items || []).reduce((sum, i) => sum + Math.max(0, Number(i.quantity) || 0), 0);
  return `Marks the shipment as dispatched without moving any stock: the ${units.toLocaleString('en-IN')} unit${units === 1 ? '' : 's'} `
    + 'stay where your manual stock entry put them and no PO dispatched quantity changes. Its reserved stock is released.';
};

// Roles allowed to mark a shipment dispatched without moving stock (the server checks too).
const MANUAL_DISPATCH_ROLES = ['admin', 'manager'];

const REGION_COLORS = {
  Bengaluru: '#5856D6', Mumbai: '#FF9500', Hyderabad: '#AF52DE',
  Gurgaon: '#007AFF', Chennai: '#34C759',
};

const TH_CLS = 'px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500';

const TH = ({ children, align = 'left', className = '' }) => (
  <th className={`text-${align} ${TH_CLS} ${className}`}>{children}</th>
);

/** Raw text from the "available containers" field -> integer 1..10, or an error message. */
const parseMaxContainers = (raw) => {
  if (String(raw).trim() === '') return { error: 'Enter how many containers are available (1–10).' };
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 10) return { error: 'Use a whole number from 1 to 10.' };
  return { value: n };
};

const FCDemandTab = ({ canEdit }) => {
  const navigate = useNavigate();
  const [demand, setDemand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planningFc, setPlanningFc] = useState(null);
  const [optimizeFc, setOptimizeFc] = useState(null);
  // Raw text so the field can be cleared while typing; parsed/validated before use.
  const [maxContainersRaw, setMaxContainersRaw] = useState('1');
  const containersCheck = parseMaxContainers(maxContainersRaw);
  const maxContainers = containersCheck.value;
  const [optimizing, setOptimizing] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const fetchDemand = useCallback(() => {
    return axios.get(`${API}/shipment-planning/fc-demand`, { headers: getAuthHeaders() })
      .then(res => { setDemand(res.data); setLoadError(null); })
      .catch((e) => setLoadError(e))
      .finally(() => { setLoading(false); setRetrying(false); });
  }, []);
  useEffect(() => { fetchDemand(); }, [fetchDemand]);
  const retryDemand = () => { setRetrying(true); fetchDemand(); };

  const planShipment = async (fc) => {
    setPlanningFc(fc.fc_code);
    try {
      const res = await axios.post(
        `${API}/shipment-planning/consolidate`,
        { fc_code: fc.fc_code, container_type: '32FT', round_to_carton: true },
        { headers: getAuthHeaders() }
      );
      (res.data.warnings || []).slice(0, 3).forEach(w => toast.warning(w));
      navigate(`/shipment-planning/consolidation/${res.data.allocation_plan_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create consolidation plan');
    } finally {
      setPlanningFc(null);
    }
  };

  const runCompare = async () => {
    if (!maxContainers) return;
    setComparing(true);
    setCompareResult(null);
    try {
      const res = await axios.post(
        `${API}/shipment-planning/compare`,
        { fc_code: optimizeFc.fc_code, container_type: '32FT', max_containers: maxContainers },
        { headers: getAuthHeaders() }
      );
      setCompareResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  const runOptimize = async () => {
    if (!maxContainers) return;
    setOptimizing(true);
    try {
      const res = await axios.post(
        `${API}/shipment-planning/optimize`,
        { fc_code: optimizeFc.fc_code, container_type: '32FT', max_containers: maxContainers },
        { headers: getAuthHeaders() }
      );
      (res.data.warnings || []).slice(0, 3).forEach(w => toast.warning(w));
      setOptimizeFc(null);
      navigate(`/shipment-planning/consolidation/${res.data.allocation_plan_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  if (loading) return <Loading label="Loading FC demand…" />;
  if (loadError && demand.length === 0) {
    return <LoadError what="FC demand" error={loadError} onRetry={retryDemand} retrying={retrying} testId="fc-demand-load-error" />;
  }

  const maxRemaining = demand.length ? Math.max(...demand.map(d => d.remaining_qty)) : 1;

  return (
    <div className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm">
      <div className="px-5 py-3 border-b border-black/5 bg-gray-50/70 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Open PO Demand by FC</span>
        <span className="text-[11px] text-gray-500">Consolidates all open vendor POs per FC into one shipment plan</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] sp-grid-table" aria-label="FC demand and planning commitments" data-testid="fc-demand-table">
          <thead>
            <tr>
              <TH>FC</TH>
              <TH>City / Region</TH>
              <TH align="right">Open POs</TH>
              <TH align="right">Remaining Units</TH>
              <TH align="right"><Term hint={PLANNER_TERMS.draftReserved}>Draft reserved</Term></TH>
              <TH align="right"><Term hint={PLANNER_TERMS.committed}>Committed</Term></TH>
              <TH align="right"><Term hint={PLANNER_TERMS.unplanned}>Unplanned (matched)</Term></TH>
              <TH align="right">Value</TH>
              <TH align="left">Demand</TH>
              {canEdit && <TH align="right">Actions</TH>}
            </tr>
          </thead>
          <tbody>
            {demand.map((d) => {
              const color = REGION_COLORS[d.region_name] || '#8E8E93';
              const canPlan = d.unplanned_qty > 0;
              return (
                <tr key={d.fc_code} data-testid={`fc-demand-row-${d.fc_code}`}>
                  <td className="px-4 py-3">
                    <span className="sp-num px-2 py-0.5 rounded-md text-[11px] font-bold text-white" style={{ background: color }}>{d.fc_code}</span>
                    {d.fc_type === 'IXD' && <Term hint={PLANNER_TERMS.ixd} className="ml-1.5 text-[10px] font-bold text-purple-700 uppercase">IXD</Term>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{d.city || '—'}{d.region_name ? `, ${d.region_name}` : ''}</td>
                  <td className="px-4 py-3 text-right sp-num font-semibold">{d.open_pos}</td>
                  <td className="px-4 py-3 text-right sp-num font-bold text-amber-700">{d.remaining_qty.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right sp-num" data-testid={`fc-reserved-${d.fc_code}`}>{(d.reserved_qty || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right sp-num" data-testid={`fc-committed-${d.fc_code}`}>{(d.committed_qty || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right sp-num" data-testid={`fc-unplanned-${d.fc_code}`}>
                    {canPlan ? (
                      <span className="font-semibold text-green-700">{d.unplanned_qty.toLocaleString('en-IN')}</span>
                    ) : (
                      <span className="text-gray-500">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right sp-num">₹{(d.total_value / 100000).toFixed(1)}L</td>
                  <td className="px-4 py-3 w-36">
                    <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(d.remaining_qty / maxRemaining) * 100}%`, background: color }} />
                    </div>
                  </td>
                  {canEdit && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setMaxContainersRaw('1'); setOptimizeFc(d); }}
                        disabled={!canPlan}
                        title={canPlan ? 'Optimize unplanned demand' : 'No unplanned matched demand; review existing plans'}
                        className="h-7 px-2.5 text-[11px] font-bold rounded-lg border-[#AF52DE]/40 text-[#AF52DE] hover:bg-purple-50 active:scale-95 transition-transform disabled:opacity-40"
                        data-testid={`optimize-fc-${d.fc_code}`}
                      >
                        <Sparkles className="h-3 w-3 mr-1" /> Optimize
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => planShipment(d)}
                        disabled={!canPlan || planningFc === d.fc_code}
                        title={canPlan ? 'Generate consolidation plan' : 'No unplanned matched demand; review existing plans'}
                        className={`h-7 px-3 text-[11px] font-bold rounded-lg active:scale-95 transition-transform ${canPlan ? 'bg-[#007AFF] hover:bg-[#005bb5] text-white' : 'bg-gray-100 text-gray-400'}`}
                        data-testid={`plan-fc-${d.fc_code}`}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        {planningFc === d.fc_code ? 'Planning…' : 'Plan Shipment'}
                      </Button>
                    </div>
                  </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {demand.length === 0 && (
        <div className="text-center py-14 text-gray-500 text-[13px]">
          <PackageSearch className="h-8 w-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
          No open PO demand. Import your Amazon PO Export first.
        </div>
      )}

      {/* Optimize / Compare dialog */}
      <Dialog open={!!optimizeFc} onOpenChange={(o) => { if (!o) { setOptimizeFc(null); setCompareResult(null); } }}>
        <DialogContent className={compareResult ? 'max-w-3xl' : 'max-w-md'} data-testid="optimize-dialog">
          <DialogHeader>
            <DialogTitle className="sp-heading text-[18px] font-black flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#AF52DE]" /> OPTIMIZE {optimizeFc?.fc_code}
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Picks the mix of PO quantities with the highest value, favouring POs with the nearest deadlines, that fits in the trucks you have.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label htmlFor="max-containers-input" className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Available Containers (32FT)</label>
              <div className="flex items-center gap-2 mt-2">
                {[1, 2, 3, 4].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { setMaxContainersRaw(String(n)); setCompareResult(null); }}
                    aria-pressed={maxContainers === n}
                    className={`h-10 w-12 rounded-lg text-[14px] sp-num font-bold border transition-colors ${maxContainers === n ? 'bg-[#AF52DE] text-white border-[#AF52DE]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#AF52DE]/50'}`}
                    data-testid={`max-containers-${n}`}
                  >
                    {n}
                  </button>
                ))}
                <Input
                  id="max-containers-input"
                  type="number" min={1} max={10} step={1} inputMode="numeric" value={maxContainersRaw}
                  onChange={(e) => { setMaxContainersRaw(e.target.value); setCompareResult(null); }}
                  aria-invalid={!!containersCheck.error}
                  aria-describedby={containersCheck.error ? 'max-containers-error' : undefined}
                  className={`h-10 w-20 text-center sp-num font-bold text-[14px] ${containersCheck.error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  data-testid="max-containers-input"
                />
              </div>
              {containersCheck.error && (
                <p id="max-containers-error" className="mt-1.5 text-[12px] text-red-700" data-testid="max-containers-error">{containersCheck.error}</p>
              )}
            </div>
            <div className="text-[11px] text-gray-500">
              {optimizeFc && <>Unplanned: <span className="sp-num font-semibold text-gray-600" data-testid="optimizer-unplanned-qty">{optimizeFc.unplanned_qty?.toLocaleString('en-IN')}</span> matched units</>}
            </div>

            {/* Comparison result */}
            {compareResult && (
              <div className="grid grid-cols-2 gap-3" data-testid="compare-result">
                {[
                  { key: 'rule', label: 'RULE-BASED', color: '#007AFF', action: () => { setOptimizeFc(null); setCompareResult(null); planShipment(optimizeFc); }, testId: 'use-rule-plan-btn' },
                  { key: 'optimized', label: '✦ OPTIMIZED', color: '#AF52DE', action: runOptimize, testId: 'use-optimized-plan-btn' },
                ].map(side => {
                  const m = compareResult[side.key];
                  const other = compareResult[side.key === 'rule' ? 'optimized' : 'rule'];
                  const betterValue = m.value > other.value;
                  const fewerContainers = m.container_count < other.container_count;
                  return (
                    <div key={side.key} className="sp-card rounded-xl p-4 space-y-2" data-testid={`compare-${side.key}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-black tracking-wide" style={{ color: side.color }}>{side.label}</span>
                        {m.solver_status && <span className="text-[10px] text-gray-500">{m.solver_status} · {m.solve_time_ms}ms</span>}
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                        <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Units</div><span className="sp-num font-bold text-gray-800">{m.planned_units}</span><span className="text-gray-500">/{m.outstanding_units}</span> <span className="text-gray-500">({m.coverage_pct}%)</span></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Value</div><span className={`sp-num font-bold ${betterValue ? 'text-green-700' : 'text-gray-800'}`}>₹{(m.value / 100000).toFixed(2)}L</span></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Containers</div><span className={`sp-num font-bold ${fewerContainers ? 'text-green-700' : 'text-gray-800'}`}>{m.container_count}</span></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Cartons / CBM</div><span className="sp-num font-bold text-gray-800">{m.cartons}</span> <span className="text-gray-500">/ {m.cbm?.toFixed(1)}</span></div>
                        <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-gray-500">Urgent PO Coverage (≤21d deadline)</div><span className="sp-num font-bold text-gray-800">{m.urgent_coverage_pct != null ? `${m.urgent_coverage_pct}%` : '—'}</span> {m.urgent_coverage_pct != null && <span className="text-gray-500">({m.urgent_shipped}/{m.urgent_outstanding})</span>}</div>
                      </div>
                      <div className="space-y-0.5">
                        {(m.warnings || []).slice(0, 2).map((w) => (
                          <p key={w} className="text-[11px] text-amber-800 leading-snug">⚠ {w}</p>
                        ))}
                      </div>
                      <Button
                        onClick={side.action}
                        disabled={optimizing || !maxContainers}
                        className="w-full h-8 text-[11px] font-bold rounded-lg text-white active:scale-[0.98] transition-transform"
                        style={{ background: side.color }}
                        data-testid={side.testId}
                      >
                        Use This Plan
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                onClick={runCompare}
                disabled={comparing || optimizing || !maxContainers}
                variant="outline"
                className="flex-1 h-10 text-[12px] font-bold rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition-transform"
                data-testid="run-compare-btn"
              >
                {comparing ? 'Comparing…' : 'Compare Rule vs Optimizer'}
              </Button>
              <Button
                onClick={runOptimize}
                disabled={optimizing || comparing || !maxContainers}
                className="flex-1 h-10 bg-[#AF52DE] hover:bg-[#9542BD] active:scale-[0.98] text-white text-[12px] font-bold rounded-lg transition-transform"
                data-testid="run-optimize-btn"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                {optimizing ? 'Solving…' : 'Run Optimizer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const planTypeLabel = (p) => (p.plan_type === 'FC_CONSOLIDATION' ? 'FC consolidation' : 'PO split');

const PlansTab = ({ canEdit }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ACTIVE');

  const fetchPlans = useCallback(() => {
    return axios.get(`${API}/shipment-planning/allocation-plans`, { headers: getAuthHeaders(), params: { limit: PLAN_FETCH_LIMIT } })
      .then(res => { setPlans(res.data); setLoadError(null); })
      .catch((e) => setLoadError(e))
      .finally(() => { setLoading(false); setRetrying(false); });
  }, []);
  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  const retryPlans = () => { setRetrying(true); fetchPlans(); };

  const statusRows = useMemo(() => plans.filter((p) => {
    if (statusFilter === 'ALL') return true;
    if (statusFilter === 'ACTIVE') return p.planning_status !== 'CANCELLED';
    return p.planning_status === statusFilter;
  }), [plans, statusFilter]);
  const table = useTableControls(statusRows, {
    searchKeys: PLAN_SEARCH_KEYS, initialSort: { key: 'planning_date', dir: 'desc' }, pageSize: 25, sortValue: PLAN_SORT_VALUE,
  });

  if (loading) return <Loading label="Loading plans…" />;
  if (loadError && plans.length === 0) {
    return <LoadError what="allocation plans" error={loadError} onRetry={retryPlans} retrying={retrying} testId="plans-load-error" />;
  }

  const hiddenCancelled = statusFilter === 'ACTIVE' ? plans.length - statusRows.length : 0;

  return (
    <div className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm">
      {loadError && (
        <LoadError what="the latest allocation plans" error={loadError} onRetry={retryPlans} retrying={retrying} className="m-4" testId="plans-load-error" />
      )}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-black/5 bg-gray-50/70" data-testid="plans-filters">
        <div className="relative w-full sm:w-auto">
          <Search className="h-3.5 w-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input
            value={table.query}
            onChange={(e) => table.setQuery(e.target.value)}
            placeholder="Search PO, FC or platform"
            aria-label="Search plans by PO number, FC or platform"
            className="h-9 w-full sm:w-60 pl-8 text-[12px] bg-white"
            data-testid="plans-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); table.setPage(1); }}>
          <SelectTrigger className="h-9 w-52 text-[12px] bg-white" aria-label="Filter plans by status" data-testid="plans-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PLAN_STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value} className="text-[12px]">{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {hiddenCancelled > 0 && (
          <span className="text-[11px] text-gray-600">{hiddenCancelled} cancelled plan{hiddenCancelled === 1 ? '' : 's'} hidden</span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] sp-grid-table" aria-label="Allocation plans" data-testid="plans-table">
          <thead>
            <tr className="bg-gray-50/70 text-gray-500">
              <SortHeader label="PO / FC" sortKey="po_number" table={table} className={TH_CLS} />
              <TH>Type</TH>
              <TH>Platform</TH>
              <SortHeader label="Planned" sortKey="planning_date" table={table} className={TH_CLS} />
              <TH align="right">PO Units</TH>
              <SortHeader label="Suggested" sortKey="total_suggested_units" table={table} align="right" className={TH_CLS} />
              <TH align="right">Cartons</TH>
              <SortHeader label="Fill %" sortKey="container_fill_percentage" table={table} align="right" className={TH_CLS} />
              <SortHeader label="Status" sortKey="planning_status" table={table} className={TH_CLS} />
              <TH align="right">Actions</TH>
            </tr>
          </thead>
          <tbody>
            {table.pageRows.map((p, i) => (
              <tr key={p.id} className={p.planning_status === 'CANCELLED' ? 'text-gray-500' : ''} data-testid={`plan-row-${i}`}>
                <td className="px-4 py-3">
                  <div className="sp-num font-semibold text-gray-800">{p.plan_type === 'FC_CONSOLIDATION' ? (p.fc_code || p.po_number) : p.po_number}</div>
                  {p.plan_type === 'FC_CONSOLIDATION' ? (
                    <div className="text-[11px] text-gray-500">{(p.po_numbers || []).length} PO(s){p.container_count ? ` · ${p.container_count} container(s)` : ''}</div>
                  ) : p.fc_code ? <div className="text-[11px] text-gray-500">{p.fc_code}</div> : null}
                </td>
                <td className="px-4 py-3 text-gray-600">{planTypeLabel(p)}</td>
                <td className="px-4 py-3 text-gray-600">{p.platform ? platformLabel(p.platform) : '—'}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.planning_date?.split('T')[0]}</td>
                <td className="px-4 py-3 text-right sp-num">{p.total_po_units}</td>
                <td className="px-4 py-3 text-right sp-num font-semibold text-[#0062CC]">{p.total_suggested_units}</td>
                <td className="px-4 py-3 text-right sp-num">{p.total_cartons}</td>
                <td className="px-4 py-3 text-right sp-num">{p.container_fill_percentage?.toFixed(1)}%</td>
                <td className="px-4 py-3">
                  <StatusBadge status={p.planning_status} data-testid={`plan-status-${i}`} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => navigate(p.plan_type === 'FC_CONSOLIDATION' ? `/shipment-planning/consolidation/${p.id}` : `/shipment-planning/plan/${encodeURIComponent(p.po_number)}?planId=${p.id}`)} className="h-7 px-2.5 text-[11px] font-semibold" data-testid={`view-plan-${i}`}>
                      <Eye className="h-3 w-3 mr-1" aria-hidden="true" /> View
                    </Button>
                    {canEdit && p.planning_status === 'DRAFT' && <CancelDraftButton planId={p.id} onCancelled={fetchPlans} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {plans.length === 0 ? (
        <div className="text-center py-14 text-gray-500 text-[13px]">No allocation plans yet. Use &quot;Plan Shipment&quot; on the FC Demand tab or a Retail PO.</div>
      ) : table.total === 0 ? (
        <div className="text-center py-10 text-gray-500 text-[13px]" data-testid="plans-no-match">No plans match these filters.</div>
      ) : null}
      <TablePager table={table} itemLabel="plans" className="border-t border-black/5" />
      {plans.length >= PLAN_FETCH_LIMIT && (
        <p className="px-4 pb-3 text-[11px] text-gray-600">Showing the latest {PLAN_FETCH_LIMIT.toLocaleString('en-IN')} plans; older plans are not listed.</p>
      )}
    </div>
  );
};

const ShipmentsTab = ({ canEdit }) => {
  const { user } = useAuth();
  const canSkipStockMove = MANUAL_DISPATCH_ROLES.includes(user?.role);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [fcFilter, setFcFilter] = useState('ALL');
  // { shipment, status } awaiting confirmation (DISPATCHED moves stock; CANCELLED / DELIVERED are final)
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  // Server's reason a confirmed change was refused (e.g. which SKUs are short), shown in the dialog
  const [statusError, setStatusError] = useState(null);
  // Dispatch only: the stock was already moved and recorded by hand, so just mark it dispatched.
  const [skipStockMove, setSkipStockMove] = useState(false);

  const fetchShipments = useCallback(() => {
    axios.get(`${API}/shipment-planning/fc-shipments`, { headers: getAuthHeaders(), params: { limit: SHIPMENT_FETCH_LIMIT } })
      .then(res => { setShipments(res.data); setLoadError(null); })
      .catch((e) => setLoadError(e))
      .finally(() => { setLoading(false); setRetrying(false); });
  }, []);

  useEffect(() => { fetchShipments(); }, [fetchShipments]);
  const retryShipments = () => { setRetrying(true); fetchShipments(); };

  const fcOptions = useMemo(() => [...new Set(shipments.map((s) => s.fc_code).filter(Boolean))].sort(), [shipments]);
  const filteredRows = useMemo(() => shipments.filter((s) => {
    if (fcFilter !== 'ALL' && s.fc_code !== fcFilter) return false;
    if (statusFilter === 'ALL') return true;
    if (statusFilter === 'OPEN') return OPEN_SHIPMENT_STATUSES.includes(s.status);
    return s.status === statusFilter;
  }), [shipments, statusFilter, fcFilter]);
  // No initial sort: keep the server's newest-first order until a header is clicked.
  const table = useTableControls(filteredRows, { searchKeys: SHIPMENT_SEARCH_KEYS, pageSize: 25, sortValue: SHIPMENT_SORT_VALUE });

  // Rethrows so a ConfirmDialog stays open when the server rejects the change.
  const applyStatus = async (shipmentId, status, { moveStock = true } = {}) => {
    try {
      const params = { status, ...(moveStock ? {} : { move_stock: false }) };
      const res = await axios.put(`${API}/shipment-planning/fc-shipments/${shipmentId}/status`, {}, { headers: getAuthHeaders(), params });
      toast.success(res.data?.message || `Status updated to ${statusLabel(status)}`);
      fetchShipments();
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to update status'));
      throw e;
    }
  };

  const updateStatus = (shipment, status) => {
    if (status === shipment.status) return;
    if (status === 'DISPATCHED' || status === 'CANCELLED' || status === 'DELIVERED') {
      setPendingStatus({ shipment, status });
      setStatusError(null);
      setSkipStockMove(false);
      setStatusDialogOpen(true);
      return;
    }
    applyStatus(shipment.shipment_id, status).catch(() => {});
  };

  if (loading) return <Loading label="Loading FC shipments…" />;
  if (loadError && shipments.length === 0) {
    return <LoadError what="FC shipments" error={loadError} onRetry={retryShipments} retrying={retrying} testId="shipments-load-error" />;
  }

  const pending = pendingStatus?.shipment;
  const pendingCancel = pendingStatus?.status === 'CANCELLED';
  const pendingDispatch = pendingStatus?.status === 'DISPATCHED';
  const manualDispatch = pendingDispatch && canSkipStockMove && skipStockMove;

  return (
    <div className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm">
      {loadError && (
        <LoadError what="the latest FC shipments" error={loadError} onRetry={retryShipments} retrying={retrying} className="m-4" testId="shipments-load-error" />
      )}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-black/5 bg-gray-50/70" data-testid="shipments-filters">
        <div className="relative w-full sm:w-auto">
          <Search className="h-3.5 w-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input
            value={table.query}
            onChange={(e) => table.setQuery(e.target.value)}
            placeholder="Search shipment, PO, FC or city"
            aria-label="Search shipments by shipment ID, PO number, FC or city"
            className="h-9 w-full sm:w-64 pl-8 text-[12px] bg-white"
            data-testid="shipments-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); table.setPage(1); }}>
          <SelectTrigger className="h-9 w-56 text-[12px] bg-white" aria-label="Filter shipments by status" data-testid="shipments-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SHIPMENT_STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value} className="text-[12px]">{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fcFilter} onValueChange={(v) => { setFcFilter(v); table.setPage(1); }}>
          <SelectTrigger className="h-9 w-36 text-[12px] bg-white" aria-label="Filter shipments by FC" data-testid="shipments-fc-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" className="text-[12px]">All FCs</SelectItem>
            {fcOptions.map((fc) => <SelectItem key={fc} value={fc} className="text-[12px]">{fc}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] sp-grid-table" aria-label="FC shipments" data-testid="shipments-table">
          <thead>
            <tr className="bg-gray-50/70 text-gray-500">
              <th className="w-8"><span className="sr-only">Details</span></th>
              <SortHeader label="Shipment ID" sortKey="shipment_id" table={table} className={TH_CLS} />
              <SortHeader label="FC" sortKey="fc_code" table={table} className={TH_CLS} />
              <TH>City</TH>
              <TH>PO</TH>
              <SortHeader label="Units" sortKey="total_units" table={table} align="right" className={TH_CLS} />
              <TH align="right">Cartons</TH>
              <TH align="right"><Term hint={PLANNER_TERMS.cbm}>CBM</Term></TH>
              <SortHeader label="Value" sortKey="total_value" table={table} align="right" className={TH_CLS} />
              <SortHeader label="Status" sortKey="status" table={table} className={TH_CLS} />
            </tr>
          </thead>
          <tbody>
            {table.pageRows.map((s, i) => {
              const nextStatuses = NEXT_STATUSES[s.status] || [];
              return (
              <React.Fragment key={s.id}>
                <tr data-testid={`shipment-row-${i}`}>
                  <td className="pl-2">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                      className="h-6 w-6"
                      aria-label={`${expanded === s.id ? 'Hide' : 'Show'} items in shipment ${s.shipment_id}`}
                      aria-expanded={expanded === s.id}
                      data-testid={`expand-shipment-${i}`}
                    >
                      {expanded === s.id ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
                    </Button>
                  </td>
                  <td className="px-4 py-3 sp-num text-[11px] font-semibold text-gray-700">{s.shipment_id}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="sp-num px-2 py-0.5 rounded-md text-[11px] font-bold text-white" style={{ background: REGION_COLORS[s.region] || '#6B7280' }}>
                      {s.fc_code}
                    </span>
                    {s.container_number > 0 && (s.po_numbers || []).length > 0 && (
                      <span className="ml-1.5 sp-num text-[10px] font-bold text-gray-600">C{s.container_number}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.city}</td>
                  <td className="px-4 py-3 sp-num text-[11px] text-gray-600">{(s.po_numbers || []).length > 1 ? `${s.po_numbers.length} POs` : (s.po_numbers?.[0] || s.po_number)}</td>
                  <td className="px-4 py-3 text-right sp-num font-semibold">{s.total_units}</td>
                  <td className="px-4 py-3 text-right sp-num">{s.total_cartons}</td>
                  <td className="px-4 py-3 text-right sp-num">{s.total_cbm?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right sp-num">₹{s.total_value?.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">
                    {canEdit && nextStatuses.length > 0 ? (
                      <Select value={s.status} onValueChange={(val) => updateStatus(s, val)}>
                        <SelectTrigger
                          className="h-7 w-auto min-w-[120px] gap-1 border-0 bg-transparent px-1 text-[11px] shadow-none"
                          aria-label={`Change status of shipment ${s.shipment_id} (currently ${statusLabel(s.status)})`}
                          data-testid={`shipment-status-select-${i}`}
                        >
                          <StatusBadge status={s.status} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={s.status} className="text-[12px]" disabled>{statusLabel(s.status)} (current)</SelectItem>
                          {nextStatuses.map(st => (
                            <SelectItem key={st} value={st} className="text-[12px]">Mark as {statusLabel(st).toLowerCase()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <StatusBadge status={s.status} data-testid={`shipment-status-${i}`} />
                    )}
                  </td>
                </tr>
                {expanded === s.id && (
                  <tr className="bg-blue-50/40">
                    <td colSpan="10" className="px-4 py-4 pl-12">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="text-left pb-2 text-[10px] font-bold uppercase tracking-wider">PO</th>
                            <th className="text-left pb-2 text-[10px] font-bold uppercase tracking-wider">SKU</th>
                            <th className="text-left pb-2 text-[10px] font-bold uppercase tracking-wider">Product</th>
                            <th className="text-right pb-2 text-[10px] font-bold uppercase tracking-wider">Qty</th>
                            <th className="text-right pb-2 text-[10px] font-bold uppercase tracking-wider">Cartons</th>
                            <th className="text-right pb-2 text-[10px] font-bold uppercase tracking-wider">CBM</th>
                            <th className="text-right pb-2 text-[10px] font-bold uppercase tracking-wider">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(s.items || []).map((it) => (
                            <tr key={`${it.po_number || ''}-${it.sku_code}`} className="border-t border-blue-100">
                              <td className="py-2 sp-num text-[10px] text-gray-600">{it.po_number || '—'}</td>
                              <td className="py-2 sp-num font-medium">{it.sku_code}</td>
                              <td className="py-2 text-gray-600">{it.product_name}</td>
                              <td className="py-2 text-right sp-num">{it.quantity}</td>
                              <td className="py-2 text-right sp-num">{it.cartons}</td>
                              <td className="py-2 text-right sp-num">{it.cbm?.toFixed(2)}</td>
                              <td className="py-2 text-right sp-num">₹{it.value?.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {shipments.length === 0 ? (
        <div className="text-center py-14 text-gray-500 text-[13px]">No FC shipments yet. Approve an allocation plan to create shipments.</div>
      ) : table.total === 0 ? (
        <div className="text-center py-10 text-gray-500 text-[13px]" data-testid="shipments-no-match">No shipments match these filters.</div>
      ) : null}
      <TablePager table={table} itemLabel="shipments" className="border-t border-black/5" />
      {shipments.length >= SHIPMENT_FETCH_LIMIT && (
        <p className="px-4 pb-3 text-[11px] text-gray-600">Showing the latest {SHIPMENT_FETCH_LIMIT.toLocaleString('en-IN')} shipments; older shipments are not listed.</p>
      )}

      <ConfirmDialog
        open={statusDialogOpen && !!pendingStatus}
        onOpenChange={setStatusDialogOpen}
        title={pendingDispatch ? `Dispatch shipment ${pending?.shipment_id}?`
          : pendingCancel ? `Cancel shipment ${pending?.shipment_id}?` : `Mark shipment ${pending?.shipment_id} as delivered?`}
        description={pendingDispatch
          ? `${manualDispatch ? manualDispatchSummary(pending) : dispatchSummary(pending)} A dispatched shipment can't be cancelled.`
          : pendingCancel
            ? 'Its reserved stock and PO quantity will be released for re-planning. This cannot be undone.'
            : 'Delivered is a final status. The shipment\'s status cannot be changed afterwards.'}
        confirmLabel={manualDispatch ? 'Mark as dispatched' : pendingDispatch ? 'Dispatch shipment' : pendingCancel ? 'Cancel shipment' : 'Mark as delivered'}
        cancelLabel={pendingCancel ? 'Keep shipment' : 'Not yet'}
        busyLabel={pendingDispatch ? 'Dispatching…' : 'Updating…'}
        destructive={pendingCancel}
        onConfirm={async () => {
          setStatusError(null);
          try {
            await applyStatus(pending.shipment_id, pendingStatus.status, { moveStock: !manualDispatch });
          } catch (e) {
            setStatusError(errorMessage(e, 'Failed to update status'));
            throw e; // keeps the dialog open
          }
        }}
        testId={pendingDispatch ? 'dispatch-shipment-dialog' : pendingCancel ? 'cancel-shipment-dialog' : 'deliver-shipment-dialog'}
      >
        {pending && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-black/5 bg-gray-50 p-3 text-[13px]" data-testid="shipment-status-summary">
            <dt className="text-gray-500">FC</dt>
            <dd className="text-right font-semibold text-gray-900">{pending.fc_code}{pending.container_number > 0 ? ` · C${pending.container_number}` : ''}</dd>
            <dt className="text-gray-500">Units / cartons</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">{(pending.total_units || 0).toLocaleString('en-IN')} / {(pending.total_cartons || 0).toLocaleString('en-IN')}</dd>
            <dt className="text-gray-500">Value</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">₹{Math.round(pending.total_value || 0).toLocaleString('en-IN')}</dd>
            <dt className="text-gray-500">Status</dt>
            <dd className="text-right font-semibold text-gray-900">{statusLabel(pending.status)} → {statusLabel(pendingStatus.status)}</dd>
          </dl>
        )}
        {pendingDispatch && canSkipStockMove && (
          <label className="flex items-start gap-2 rounded-lg border border-black/5 p-3 text-[13px] text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={skipStockMove}
              onChange={(e) => setSkipStockMove(e.target.checked)}
              data-testid="dispatch-skip-stock-move"
            />
            <span>Stock already moved (recorded manually) — just mark as dispatched</span>
          </label>
        )}
        {statusError && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-700" data-testid="shipment-status-error">
            {statusError}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
};

/** Raw share text -> number 0..100, or an error message. */
const parseShare = (raw) => {
  if (String(raw ?? '').trim() === '') return { error: 'Enter a share from 0 to 100.' };
  const n = Number(raw);
  if (!Number.isFinite(n)) return { error: 'Enter a number.' };
  if (n < 0 || n > 100) return { error: 'Share must be between 0 and 100.' };
  return { value: n };
};

const FCSettingsTab = ({ canEdit, user }) => {
  const [fcs, setFcs] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  // Unsaved share edits: { [fcId]: rawString }
  const [shareEdits, setShareEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  // Active/Type change awaiting confirmation: { fc, changes }
  const [pendingChange, setPendingChange] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [fcRes, boxRes] = await Promise.all([
        axios.get(`${API}/shipment-planning/fc-regions?platform=AMAZON`, { headers: getAuthHeaders() }),
        axios.get(`${API}/shipment-planning/carton-boxes`, { headers: getAuthHeaders() })
      ]);
      setFcs(fcRes.data);
      setBoxes(boxRes.data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  const retryLoad = () => { setRetrying(true); fetchData(); };

  // Rethrows so the confirm dialog stays open on failure.
  const updateFC = async (fc, changes, successText) => {
    try {
      await axios.put(`${API}/shipment-planning/fc-regions/${fc.id}/settings`, changes, { headers: getAuthHeaders() });
      toast.success(successText || `${fc.fc_code} updated`);
      await fetchData();
    } catch (e) {
      toast.error(errorMessage(e, `Couldn't update ${fc.fc_code}`));
      throw e;
    }
  };

  const saveShare = async (fc) => {
    const check = parseShare(shareEdits[fc.id]);
    if (check.error) return;
    setSavingId(fc.id);
    try {
      await updateFC(fc, { default_sales_share_percentage: check.value }, `${fc.fc_code} share set to ${check.value}%`);
      setShareEdits((prev) => { const n = { ...prev }; delete n[fc.id]; return n; });
    } catch {
      // toast already shown; keep the edit so the user can fix and retry
    } finally {
      setSavingId(null);
    }
  };

  const discardShare = (fcId) => setShareEdits((prev) => { const n = { ...prev }; delete n[fcId]; return n; });

  const shareOf = (fc) => {
    if (shareEdits[fc.id] === undefined) return fc.default_sales_share_percentage || 0;
    return parseShare(shareEdits[fc.id]).value ?? (fc.default_sales_share_percentage || 0);
  };
  const activeShareTotal = fcs.filter(f => f.active_status).reduce((s, f) => s + (f.default_sales_share_percentage || 0), 0);
  const dirtyIds = Object.keys(shareEdits).filter((id) => {
    const fc = fcs.find((f) => String(f.id) === id);
    return fc && String(shareEdits[id]) !== String(fc.default_sales_share_percentage ?? '');
  });
  const projectedTotal = fcs.filter(f => f.active_status).reduce((s, f) => s + shareOf(f), 0);
  const shareOk = Math.abs(activeShareTotal - 100) < 0.5;

  if (loading) return <Loading label="Loading FC settings…" />;
  if (loadError && fcs.length === 0) {
    return <LoadError what="FC settings" error={loadError} onRetry={retryLoad} retrying={retrying} testId="fc-settings-load-error" />;
  }

  const pfc = pendingChange?.fc;
  const pendingActive = pendingChange && 'active_status' in pendingChange.changes;
  const deactivating = pendingActive && pendingChange.changes.active_status === false;
  const otherActive = pfc ? fcs.filter((f) => f.active_status && f.id !== pfc.id).length : 0;
  let dialogTitle = '';
  let dialogDescription = '';
  if (pendingActive) {
    dialogTitle = deactivating ? `Deactivate ${pfc.fc_code}?` : `Activate ${pfc.fc_code}?`;
    dialogDescription = deactivating
      ? `New PO split and consolidation plans will no longer send stock to ${pfc.fc_code}. Its ${pfc.default_sales_share_percentage || 0}% share is spread across the ${otherActive} other active FC(s). Existing plans and shipments do not change.`
      : `New PO split and consolidation plans will include ${pfc.fc_code} with a ${pfc.default_sales_share_percentage || 0}% sales share. Existing plans and shipments do not change.`;
  } else if (pendingChange) {
    dialogTitle = `Change ${pfc.fc_code} type to ${pendingChange.changes.fc_type}?`;
    dialogDescription = 'The type labels this FC on the FC Demand and shipment screens. It does not change how quantities are split.';
  }

  return (
    <div className="space-y-6">
      {loadError && (
        <LoadError what="the latest FC settings" error={loadError} onRetry={retryLoad} retrying={retrying} testId="fc-settings-load-error" />
      )}
      {/* Share total banner */}
      <div className="sp-card sp-rise rounded-xl px-5 py-3.5 flex flex-wrap items-center gap-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">Active FC Share Total</span>
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="h-2 flex-1 max-w-xs rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${shareOk ? 'bg-[#34C759]' : 'bg-[#FF9500]'}`} style={{ width: `${Math.min(activeShareTotal, 100)}%` }} />
          </div>
          <span className={`sp-num text-[16px] font-bold ${shareOk ? 'text-green-700' : 'text-amber-700'}`} data-testid="share-total">
            {activeShareTotal.toFixed(1)}%
          </span>
        </div>
        {dirtyIds.length > 0 && (
          <span className="text-[12px] text-gray-700" data-testid="share-total-projected">
            After saving {dirtyIds.length} unsaved change{dirtyIds.length === 1 ? '' : 's'}: <span className="sp-num font-semibold">{projectedTotal.toFixed(1)}%</span>
          </span>
        )}
        {!shareOk && (
          <span className="text-[12px] text-amber-800">Active shares don't add up to 100%. Allocation scales them to 100%.</span>
        )}
      </div>

      <div className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm" style={{ animationDelay: '60ms' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] sp-grid-table" aria-label="FC settings" data-testid="fc-settings-table">
            <thead>
              <tr className="bg-gray-50/70">
                <TH>FC Code</TH>
                <TH>Location</TH>
                <TH>Region</TH>
                <TH align="center">Type</TH>
                <TH align="center" className="w-56">Sales Share %</TH>
                <TH align="center">Active</TH>
              </tr>
            </thead>
            <tbody>
              {fcs.map((fc) => {
                const raw = shareEdits[fc.id];
                const editing = raw !== undefined;
                const check = editing ? parseShare(raw) : {};
                const dirty = editing && String(raw) !== String(fc.default_sales_share_percentage ?? '');
                const regionColor = REGION_COLORS[fc.region_name] || '#6B7280';
                const errId = `fc-share-error-${fc.fc_code}`;
                return (
                  <tr key={fc.id} className={dirty ? 'bg-amber-50/60' : (!fc.active_status ? 'bg-gray-50 text-gray-500' : '')} data-testid={`fc-row-${fc.fc_code}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`sp-num px-2 py-0.5 rounded-md text-[11px] font-bold text-white ${!fc.active_status ? 'opacity-60' : ''}`} style={{ background: regionColor }}>{fc.fc_code}</span>
                      {!fc.active_status && <span className="ml-1.5 text-[10px] font-semibold uppercase text-gray-600">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{fc.city}, {fc.state}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        <MapPin className="h-3 w-3" style={{ color: regionColor }} aria-hidden="true" /> {fc.region_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {canEdit ? (
                        <Select value={fc.fc_type || 'FC'} onValueChange={(val) => { if (val !== (fc.fc_type || 'FC')) setPendingChange({ fc, changes: { fc_type: val } }); }}>
                          <SelectTrigger aria-label={`FC type for ${fc.fc_code}`} className={`h-7 w-[72px] mx-auto text-[10px] font-bold border-0 rounded-full ${fc.fc_type === 'IXD' ? 'bg-purple-50 text-purple-800' : 'bg-gray-100 text-gray-700'}`} data-testid={`fc-type-select-${fc.fc_code}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="FC" className="text-[12px]">FC</SelectItem>
                            <SelectItem value="IXD" className="text-[12px]">IXD</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[11px] font-bold text-gray-700">{fc.fc_type || 'FC'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {canEdit ? (
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center justify-center gap-1">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              step={0.5}
                              inputMode="decimal"
                              value={editing ? raw : (fc.default_sales_share_percentage ?? '')}
                              onChange={(e) => { const v = e.target.value; setShareEdits((prev) => ({ ...prev, [fc.id]: v })); }}
                              onKeyDown={(e) => { if (e.key === 'Enter' && dirty && !check.error) saveShare(fc); if (e.key === 'Escape') discardShare(fc.id); }}
                              aria-label={`Sales share % for ${fc.fc_code}`}
                              aria-invalid={!!check.error}
                              aria-describedby={check.error ? errId : undefined}
                              className={`h-8 w-20 text-center text-[12px] sp-num font-semibold ${check.error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              data-testid={`fc-share-input-${fc.fc_code}`}
                            />
                            {dirty && (
                              <>
                                <Button size="sm" onClick={() => saveShare(fc)} disabled={!!check.error || savingId === fc.id} className="h-8 px-2 text-[10px] font-bold bg-[#007AFF] hover:bg-[#005bb5] active:scale-95" data-testid={`fc-share-save-${fc.fc_code}`}>
                                  {savingId === fc.id ? 'Saving…' : 'Save'}
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => discardShare(fc.id)} disabled={savingId === fc.id} className="h-8 w-8" aria-label={`Discard share change for ${fc.fc_code}`} data-testid={`fc-share-discard-${fc.fc_code}`}>
                                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                                </Button>
                              </>
                            )}
                          </div>
                          {check.error ? (
                            <p id={errId} className="text-[11px] text-red-700" data-testid={errId}>{check.error}</p>
                          ) : dirty ? (
                            <p className="text-[11px] text-amber-800">Unsaved (was {fc.default_sales_share_percentage ?? 0}%)</p>
                          ) : null}
                        </div>
                      ) : (
                        <span className="sp-num font-semibold text-gray-800">{fc.default_sales_share_percentage ?? 0}%</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={fc.active_status}
                        disabled={!canEdit}
                        title={canEdit ? undefined : viewOnlyHint(user)}
                        onCheckedChange={(val) => setPendingChange({ fc, changes: { active_status: val } })}
                        aria-label={`${fc.fc_code} active for planning`}
                        data-testid={`fc-active-switch-${fc.fc_code}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingChange}
        onOpenChange={(open) => { if (!open) setPendingChange(null); }}
        title={dialogTitle}
        description={dialogDescription}
        confirmLabel={pendingActive ? (deactivating ? `Deactivate ${pfc?.fc_code}` : `Activate ${pfc?.fc_code}`) : 'Change type'}
        cancelLabel="Keep as is"
        busyLabel="Saving…"
        destructive={deactivating}
        onConfirm={() => updateFC(pfc, pendingChange.changes)}
        testId="fc-settings-confirm-dialog"
      />

      {/* Carton boxes */}
      <div className="sp-rise" style={{ animationDelay: '120ms' }}>
        <h3 className="sp-heading text-[14px] font-bold text-gray-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
          <Box className="h-4 w-4 text-[#007AFF]" aria-hidden="true" /> Carton Box Master
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="carton-boxes">
          {boxes.map(b => (
            <div key={b.id} className="sp-card sp-card-lift rounded-xl p-4">
              <div className="text-[13px] font-bold text-gray-800">{b.box_name}</div>
              <div className="sp-num text-[12px] text-gray-600 mt-1.5">
                {b.length_mm} × {b.breadth_mm} × {b.height_mm} <span className="text-gray-500">mm</span>
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5"><span className="sp-num font-semibold text-gray-700">{b.cbm}</span> CBM / carton</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function ShipmentPlanningPage() {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'demand';
  const { user } = useAuth();
  const { country } = useCountry();
  const canEdit = canEditModule(user, 'shipment_planning');

  return (
    <div className="space-y-6 sp-body" data-testid="shipment-planning-page">
      <div className="sp-rise flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl flex items-center gap-3">
            <span className="inline-flex shrink-0 items-center justify-center h-10 w-10 rounded-xl bg-[#007AFF] shadow-sm">
              <Warehouse className="h-5 w-5 text-white" strokeWidth={2} aria-hidden="true" />
            </span>
            FC Shipments
          </h1>
          <p className="mt-1.5 text-[13px] lg:text-[14px] text-gray-500">PO split plans · marketplace FC shipments · FC configuration</p>
        </div>
        {!canEdit && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-[12px] font-medium text-gray-700" title={viewOnlyHint(user)} data-testid="shipment-planning-view-only">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" /> View only
          </span>
        )}
      </div>
      {!canEdit && (
        <p className="text-[12px] text-gray-600 -mt-3" data-testid="shipment-planning-view-only-hint">{viewOnlyHint(user)} Planning, approvals, shipment status and FC settings are read-only for you.</p>
      )}

      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList className="sp-glass rounded-xl p-1 h-auto min-h-11 flex-wrap justify-start border border-black/5 shadow-sm">
          <TabsTrigger value="demand" className="text-[12px] font-semibold rounded-lg px-3 sm:px-4 h-9 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-fc-demand">
            <PackageSearch className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> FC Demand
          </TabsTrigger>
          <TabsTrigger value="plans" className="text-[12px] font-semibold rounded-lg px-3 sm:px-4 h-9 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-plans">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Plans
          </TabsTrigger>
          <TabsTrigger value="shipments" className="text-[12px] font-semibold rounded-lg px-3 sm:px-4 h-9 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-shipments">
            <Truck className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> FC Shipments
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-[12px] font-semibold rounded-lg px-3 sm:px-4 h-9 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-fc-settings">
            <Settings2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> FC Settings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="demand" className="mt-5 space-y-5"><ConsolidationSuggestions country={country} /><FCDemandTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="plans" className="mt-5"><PlansTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="shipments" className="mt-5"><ShipmentsTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="settings" className="mt-5"><FCSettingsTab canEdit={canEdit} user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}
